/*
  Xeridas motion system.

  - Smooth scroll (Lenis), driven by the GSAP ticker so scroll-linked
    animations stay in sync. Off with prefers-reduced-motion and in the
    theme editor; paused while Dawn locks the page (drawers, modals, cart).
  - Scroll reveal for [data-reveal] (see xeridas-motion.css).
  - window.XeridasMotion: shared tokens and helpers for section scripts.
      XeridasMotion.useScrollTrigger()  → registers ScrollTrigger once and wires
                                          it to Lenis; returns ScrollTrigger or null.
      XeridasMotion.reduced             → true when motion should be minimal.

  Load order (theme.liquid, all defer): gsap.min.js → ScrollTrigger.min.js →
  lenis.min.js → this file. GSAP and ScrollTrigger are only there on pages
  whose sections carry data-xeridas-gsap / data-xeridas-scrolltrigger; without
  GSAP, Lenis runs on its own requestAnimationFrame loop.
*/
(function () {
  const root = document.documentElement;
  const reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  const motion = {
    reduced: reducedQuery.matches,
    designMode,
    lenis: null,
    tokens: {
      fast: 0.6,
      base: 0.9,
      slow: 1.2,
      ease: 'power2.out',
      easeOut: 'expo.out',
      distance: 24,
      stagger: 0.09,
    },
    useScrollTrigger,
    refresh,
  };
  window.XeridasMotion = motion;

  /* ---------- Smooth scroll ---------- */

  function initLenis() {
    if (motion.reduced || designMode || typeof window.Lenis !== 'function') return;

    const lenis = new window.Lenis({
      autoRaf: false,
      lerp: 0.1,
      smoothWheel: true,
      // Lenis' own anchor handling doesn't cancel the browser's jump, so the
      // page teleported; same-page anchors are handled in initAnchors().
      anchors: false,
      // Let nested scroll areas scroll natively.
      prevent: (node) =>
        Boolean(
          node.closest &&
            node.closest('[data-lenis-prevent], .drawer, cart-drawer, .menu-drawer, details-modal, .modal, .search-modal')
        ),
    });
    motion.lenis = lenis;

    // Lenis only needs frames while it is moving the page. Driving it every
    // frame from page load kept the main thread busy during the load (Lighthouse:
    // ~1 s more blocking time on the product page), so it sleeps after a short
    // idle stretch and wakes on any scroll intent or programmatic scrollTo.
    // GSAP's ticker drives it where GSAP is on the page (ScrollTrigger stays in
    // sync); elsewhere a plain requestAnimationFrame loop does.
    const IDLE_FRAMES = 20;
    let running = false;
    let idleFrames = 0;
    let rafId = 0;

    const step = (ms) => {
      lenis.raf(ms);
      if (lenis.isScrolling) {
        idleFrames = 0;
      } else if (++idleFrames > IDLE_FRAMES) {
        sleep();
      }
    };
    const gsapStep = (time) => step(time * 1000);
    const rafLoop = (ms) => {
      step(ms);
      if (running) rafId = requestAnimationFrame(rafLoop);
    };

    function wake() {
      idleFrames = 0;
      if (running) return;
      running = true;
      // Lenis measures each frame from its previous raf call; start from zero
      // so the first frame after a pause doesn't jump the whole distance.
      lenis.time = 0;
      if (window.gsap) window.gsap.ticker.add(gsapStep);
      else rafId = requestAnimationFrame(rafLoop);
    }

    function sleep() {
      running = false;
      if (window.gsap) window.gsap.ticker.remove(gsapStep);
      else cancelAnimationFrame(rafId);
    }

    if (window.gsap) window.gsap.ticker.lagSmoothing(0);
    ['wheel', 'touchstart', 'keydown', 'scroll'].forEach((type) =>
      window.addEventListener(type, wake, { passive: true })
    );
    const scrollTo = lenis.scrollTo.bind(lenis);
    lenis.scrollTo = (...args) => {
      wake();
      return scrollTo(...args);
    };
    wake();

    // Dawn locks scrolling with body.overflow-hidden(-mobile/-tablet/-desktop).
    const syncLock = () => {
      const locked = [...document.body.classList].some((c) => c.startsWith('overflow-hidden'));
      locked ? lenis.stop() : lenis.start();
    };
    new MutationObserver(syncLock).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    syncLock();
  }

  /* ---------- Same-page anchors (e.g. header "Contáctanos" → #contacto) ----------
     Glides with an ease-in-out whose duration grows with the distance, so a
     long trip down the page reads as a calm travel, not a jump. Without Lenis
     (reduced motion, theme editor) the browser's instant jump is kept. */

  const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  function initAnchors() {
    document.addEventListener('click', (event) => {
      if (!motion.lenis || event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = event.target.closest && event.target.closest('a[href*="#"]');
      if (!link || link.target === '_blank') return;

      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !url.hash) return;

      let target;
      try {
        target = document.querySelector(decodeURIComponent(url.hash));
      } catch (error) {
        return;
      }
      if (!target) return;

      event.preventDefault();
      glideTo(target);
      if (url.hash !== window.location.hash) history.pushState(null, '', url.hash);
    });
  }

  function glideTo(target, onArrive) {
    const distance = Math.abs(target.getBoundingClientRect().top);
    const duration = Math.min(2.2, Math.max(1.1, 0.9 + distance / 5000));
    motion.lenis.scrollTo(target, { duration, easing: easeInOutCubic, onComplete: onArrive });
  }

  /* ---------- Home sections from other pages ([data-home-glide]) ----------
     Header "Contáctanos" on an inner page: Inicio → Mark Portal, not a cut
     straight into the middle of the landing. The link keeps its real href
     (/#contacto: right without JS, with reduced motion, in a new tab); here
     the click opens Inicio at the top instead and leaves the destination in
     sessionStorage. Once the home page has painted its first screen with
     the brand fonts, it glides down exactly like the same link on Inicio
     does, unless the visitor has already started scrolling. */

  const GLIDE_KEY = 'xeridas:home-glide';

  function initHomeGlideLinks() {
    document.addEventListener('click', (event) => {
      if (!motion.lenis || event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest && event.target.closest('a[data-home-glide]');
      if (!link || link.target === '_blank') return;

      const url = new URL(link.href, window.location.href);
      if (!url.hash || url.pathname === window.location.pathname) return;
      try {
        sessionStorage.setItem(GLIDE_KEY, url.hash);
      } catch (error) {
        return; // No storage: the plain /#contacto navigation still lands there.
      }
      event.preventDefault();
      window.location.assign(url.pathname + url.search);
    });
  }

  function initHomeGlideArrival() {
    let hash = null;
    try {
      hash = sessionStorage.getItem(GLIDE_KEY);
      sessionStorage.removeItem(GLIDE_KEY);
    } catch (error) {
      return;
    }
    if (!hash) return;

    let target = null;
    try {
      target = document.querySelector(decodeURIComponent(hash));
    } catch (error) {
      return;
    }
    if (!target) return;
    if (!motion.lenis) {
      target.scrollIntoView();
      return;
    }

    // The visitor takes over: scrolling, a key or a tap cancels the glide.
    let cancelled = false;
    const intents = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
    const cancel = () => (cancelled = true);
    intents.forEach((type) => window.addEventListener(type, cancel, { once: true, passive: true }));

    const fontsReady = document.fonts ? document.fonts.ready : Promise.resolve();
    const capped = Promise.race([fontsReady, new Promise((resolve) => setTimeout(resolve, 1200))]);
    capped.then(() => {
      // A beat on Inicio's first screen, so the arrival reads as a journey.
      setTimeout(() => {
        intents.forEach((type) => window.removeEventListener(type, cancel));
        if (cancelled || window.scrollY > 40) return;
        glideTo(target, () => {
          // Images that loaded during the trip can move the section a few px.
          if (Math.abs(target.getBoundingClientRect().top) > 2) {
            motion.lenis.scrollTo(target, { duration: 0.5, easing: easeInOutCubic });
          }
          history.replaceState(null, '', hash);
        });
      }, 650);
    });
  }

  /* ---------- ScrollTrigger bridge ---------- */

  let scrollTriggerReady = false;

  function useScrollTrigger() {
    if (!window.gsap || !window.ScrollTrigger) return null;
    if (!scrollTriggerReady) {
      window.gsap.registerPlugin(window.ScrollTrigger);
      if (motion.lenis) motion.lenis.on('scroll', window.ScrollTrigger.update);
      scrollTriggerReady = true;
    }
    return window.ScrollTrigger;
  }

  function refresh() {
    if (scrollTriggerReady) window.ScrollTrigger.refresh();
  }

  /* ---------- Scroll reveal ---------- */

  let revealObserver = null;

  function reveal(el) {
    el.classList.add('is-revealed');
    if (revealObserver) revealObserver.unobserve(el);
  }

  // Stagger by arrival, not by position in the markup: items of
  // [data-reveal-stagger] groups that come into view together (one observer
  // callback) follow one another in document order, across groups; an item
  // that arrives on its own starts at once. Indexes fixed by position made
  // a card scrolled into view alone on a phone (one column) still wait for
  // the cards above it: the audience selector's fourth card sat 540 ms
  // before moving. --reveal-offset / --reveal-index are written per batch.
  function revealBatch(entries) {
    const arriving = entries
      .filter((entry) => entry.isIntersecting)
      .map((entry) => entry.target)
      .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    let step = 0;
    arriving.forEach((el) => {
      if (el.closest('[data-reveal-stagger]')) {
        el.style.setProperty('--reveal-offset', 0);
        el.style.setProperty('--reveal-index', step++);
      }
      reveal(el);
    });
  }

  function initReveal(scope = document) {
    const items = [...scope.querySelectorAll('[data-reveal]:not(.is-revealed)')];
    if (!items.length) return;

    if (motion.reduced || designMode || !('IntersectionObserver' in window)) {
      items.forEach(reveal);
      return;
    }

    revealObserver =
      revealObserver ||
      new IntersectionObserver(revealBatch, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
    items.forEach((el) => revealObserver.observe(el));
  }

  /* ---------- Page transitions ----------
     xeridas-motion.css, with the pagereveal script in layout/theme.liquid
     (it has to run before the first render). Only navigations into "Quiénes
     somos" keep their view transition; every other one is skipped here, on
     the page being left, before anything is captured. A transition on every
     page change (240 ms cross-fade) made the mouse cursor drop to the system
     arrow for a moment on each navigation, which the coffee-bean cursor
     never did with plain navigations. */

  function initPageTransitions() {
    window.addEventListener('pageswap', (event) => {
      if (!event.viewTransition) return;
      const url = event.activation?.entry?.url;
      const path = url ? new URL(url).pathname : '';
      const into = path.includes('/pages/quienes-somos') && !window.location.pathname.includes('/pages/quienes-somos');
      if (!into) event.viewTransition.skipTransition();
    });
  }

  /* ---------- Boot ---------- */

  initLenis();
  initAnchors();
  initHomeGlideLinks();
  initHomeGlideArrival();
  initReveal();
  initPageTransitions();
  root.classList.remove('motion-fallback');
  window.__xeridasMotionBooted = true;

  reducedQuery.addEventListener('change', (e) => {
    motion.reduced = e.matches;
    if (e.matches && motion.lenis) {
      motion.lenis.destroy();
      motion.lenis = null;
    }
  });

  if (designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      initReveal(e.target);
      refresh();
    });
    document.addEventListener('shopify:section:reorder', refresh);
  }
})();
